import { Container, Group, Stack, Text, Title } from '@mantine/core';
import { useLocalStorage } from '@mantine/hooks';
import { createFileRoute } from '@tanstack/react-router';
import type { SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { z } from 'zod';

import { FilterOutlineIcon, ShowRightPanel, ShowTopPanel } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useCurrentLocation } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DataTable } from '@apple/ui/data-table/controls/data-table';
import { ToolbarButton } from '@apple/ui/data-table/controls/Toolbar/toolbar-button';
import { useTableState } from '@apple/ui/data-table/hooks/use-table-state';
import { TableLayout } from '@apple/ui/layouts/TableLayout';
import { Link } from '@apple/ui/link/Link';
import { ToolbarExportButton } from '@apple/utils/files/components/ToolbarExportButton';
import { globalizationQueryOptions } from '@apple/utils/globalization/queries';

import {
	buildOrderSearchRequestFilters,
	getOrderSearchExportUrl,
} from '@apple/features/order-retail/api/search';
import { TopSearchFilter } from '@apple/features/order-retail/components/TopSearchFilter';
import { useOrderSearchTable } from '@apple/features/order-retail/hooks/use-order-search-table';
import type {
	OrderSearchFilters,
	OrderSearchTableData,
} from '@apple/features/order-retail/models/search';
import { plantQueryOptions } from '@apple/features/plants/queries/queries';

const orderSearchParamsSchema = z.object({
	userId: z.string().optional(),
	/** Sales or Purchase Order Number  */
	orderNumber: z.string().optional(),
	orderStatus: z.string().optional(),
});
const defaultSorting: SortingState = [{ id: 'orderData', desc: true }];

export const Route = createFileRoute('/_authed/orders/search')({
	// TODO: Create a minimal repro for the route not loading due to this schema...
	// validateSearch: createDataTableSearchSchema<OrderSearchParams>(orderSearchParamsSchema),
	validateSearch: orderSearchParamsSchema,
	beforeLoad: () => {
		return {
			plantQueryOptions,
			countriesWithCulturesQueryOptions: globalizationQueryOptions.countriesWithCultures(),
		};
	},
	loaderDeps: ({ search }) => search,
	loader: ({
		context: { queryClient, plantQueryOptions, countriesWithCulturesQueryOptions },
	}) => {
		void queryClient.prefetchQuery(plantQueryOptions.plants);
		void queryClient.prefetchQuery(countriesWithCulturesQueryOptions);
	},
	component: OrderSearchRoute,
});

const defaultFilters: OrderSearchFilters = {
	orderDateRangeInDays: 60,
	startDate: dayjs().subtract(60, 'days').startOf('day').format('YYYY-MM-DD'),
};

function OrderSearchRoute() {
	const { t } = useTranslation('order');
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { auth } = Route.useRouteContext();
	const location = useCurrentLocation();
	const [showTopSearch, setShowTopSearch] = useState(true);
	const [switchTopSearch, setSwitchTopSearch] = useLocalStorage<boolean>({
		key: 'top-search-preference',
		defaultValue: false,
	});

	const tableState = useTableState<OrderSearchTableData, OrderSearchFilters>({
		search,
		navigate,
		defaultFilters,
		searchFilters: [
			...(search.orderNumber ? [{ id: 'orderNumber', value: search.orderNumber }] : []),
			...(search.orderStatus ? [{ id: 'orderStatus', value: search.orderStatus }] : []),
			...(search.userId ? [{ id: 'userId', value: search.userId }] : []),
		],
		defaultSorting,
		fieldMap: [],
	});

	const { table, filters, columnOrder, loading, onSetColumnOrder, isDirty } = useOrderSearchTable(
		{
			tableState,
			canAccessMultipleStores: auth.canAccessMultipleStores,
			orderLinkCell: ({ row }) => (
				<Link
					to='/orders/$poNumber'
					state={{ from: location }}
					params={{ poNumber: row.original.purchaseOrderNumber }}
					title={t('searchResults.table.links.viewOrderDetails')}
				>
					<Text size='sm'>{row.original.purchaseOrderNumber}</Text>
				</Link>
			),
		},
	);

	const headers = table.getFlatHeaders();
	const isCustomDateRange = filters.orderDateRangeInDays === 'custom';

	const exportButton = useMemo(
		() => (
			<ToolbarExportButton
				key='export'
				tooltip={t('controls:dataTable.exportResults')}
				exportUrl={getOrderSearchExportUrl()}
				fallbackFilename='OrderSearchResult.xlsx'
				filters={buildOrderSearchRequestFilters(filters)}
			/>
		),
		[t, filters],
	);

	const showTopFilter = useMemo(
		() => (
			<ToolbarButton
				key='showTopFilters'
				tooltip={t('controls:dataTable.showFilters')}
				icon={FilterOutlineIcon}
				onClick={() => setShowTopSearch(true)}
			/>
		),
		[t],
	);

	const toggleButton = useMemo(
		() => (
			<ToolbarButton
				key='switch'
				tooltip={
					switchTopSearch
						? t('controls:dataTable.rightSearchBar')
						: t('controls:dataTable.topSearchBar')
				}
				icon={switchTopSearch ? ShowRightPanel : ShowTopPanel}
				onClick={() => setSwitchTopSearch(v => !v)}
			/>
		),
		[t, switchTopSearch, setSwitchTopSearch],
	);

	if (switchTopSearch) {
		return (
			<Container fluid>
				<Stack pt={100}>
					{showTopSearch && (
						<TopSearchFilter
							headers={headers}
							isDirty={isDirty}
							isCustomDateRange={isCustomDateRange}
							onHideFiltersClick={() => setShowTopSearch(v => !v)}
							onResetFilters={() => table.resetColumnFilters()}
						/>
					)}

					<Group justify='space-between'>
						<Title order={3}>{t('searchResults.title')}</Title>
						<Group gap={0}>
							{exportButton}
							{toggleButton}
							{!showTopSearch && showTopFilter}
						</Group>
					</Group>
					<DataTable
						enableColumnReordering
						columnOrder={columnOrder}
						onColumnOrderChange={onSetColumnOrder}
						table={table}
						loading={loading}
						variant='apple-table'
						minWidth='1000px'
					/>
				</Stack>
			</Container>
		);
	}

	return (
		<TableLayout
			title={t('searchResults.title')}
			table={table}
			toolbarButtons={[exportButton, toggleButton]}
		>
			<DataTable
				enableColumnReordering
				columnOrder={columnOrder}
				onColumnOrderChange={onSetColumnOrder}
				table={table}
				loading={loading}
				variant='apple-table'
				minWidth='1000px'
			/>
		</TableLayout>
	);
}
